import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import {
    getJigsawRecordingCaptureFrame,
    RECORDING_FRAME_GOOGLE_1080P,
} from "../recordingCaptureFrame";
import { loadScreenshotJigsaw } from "../lib/api";
import {
    RECORDING_CONTENT_GRID,
    RECORDING_CONTENT_JIGSAW_ID,
    RECORDING_CONTENT_STORY_ID,
} from "../recordingContentPuzzle";
import {
    MAIN_PATH,
    recordingApplePlayPath,
    recordingPlayPath,
} from "../routes";

/**
 * Content capture entry — opens the fixed capture board
 * (Google 1920×1080 or Apple 1290×2796, from the route).
 */
export default function RecordingContentPage() {
    const { pathname } = useLocation();
    const frame =
        getJigsawRecordingCaptureFrame(pathname) ?? RECORDING_FRAME_GOOGLE_1080P;
    const isApple = frame.kind === "apple";
    const [target, setTarget] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!RECORDING_CONTENT_STORY_ID || !RECORDING_CONTENT_JIGSAW_ID) {
            setError("Recording content puzzle is not configured");
            return undefined;
        }
        let cancelled = false;
        (async () => {
            let jigsawId = RECORDING_CONTENT_JIGSAW_ID;
            try {
                const { template } = await loadScreenshotJigsaw({
                    storyId: RECORDING_CONTENT_STORY_ID,
                    expectedGrid: RECORDING_CONTENT_GRID,
                });
                if (template?.id) jigsawId = template.id;
            } catch {
                /* keep the pinned Medium template id */
            }
            if (cancelled) return;
            setTarget({ storyId: RECORDING_CONTENT_STORY_ID, jigsawId });
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (error) {
        return (
            <Box sx={{ p: 3, maxWidth: 480 }}>
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!target) {
        return (
            <Box
                sx={{
                    width: frame.width,
                    height: frame.height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#c5d5ce",
                }}
            >
                <CircularProgress size={36} />
            </Box>
        );
    }

    const playPath = isApple
        ? recordingApplePlayPath(target.storyId, target.jigsawId)
        : recordingPlayPath(target.storyId, target.jigsawId);

    return (
        <Navigate
            to={playPath}
            replace
            state={{ prototypeReturnPath: MAIN_PATH }}
        />
    );
}
